import styled from "@emotion/styled"
import { PTag } from "@porsche-design-system/components-react"
import { spacingStaticSm, spacingStaticXs } from "@porsche-design-system/components-react/emotion"
import type { ActionItem as ActionItemType, ActionItemWithLinks, ResponsiveImageView } from "@slfinrtl/ui-bff-routes"
import { useMemo } from "react"
import { Link } from "react-router"
import { useDealerAppRoutes } from "#src/common/routes/useDealerAppRoutes"
import { PictureStatic } from "src/components"
import { OpenGalleryLink } from "./OpenGalleryLink"

interface OverviewSummaryImagesProps {
	imageEdit?: ActionItemType
	images: ResponsiveImageView[]
	inventoryId: string
	onOpenGalleryClick: () => Promise<void>
	openGallery: ActionItemWithLinks
	totalImageCount: string
}

export const OverviewSummaryImages = ({
	imageEdit,
	images,
	inventoryId,
	onOpenGalleryClick,
	openGallery,
	totalImageCount,
}: OverviewSummaryImagesProps) => {
	const routes = useDealerAppRoutes()
	const imageEditRoute = useMemo(
		() => routes?.inventoryList?.vehicleDetails?.mediaEdit?.url(inventoryId),
		[inventoryId, routes?.inventoryList?.vehicleDetails?.mediaEdit],
	)

	return (
		<Wrapper>
			<PrimaryPicture>
				<PictureStatic picture={images[0]} size={960} />
				<TotalImageCountTag variant="primary">{totalImageCount}</TotalImageCountTag>
				<MediaLinksContainer>
					<OpenGalleryLink icon="external" onClick={onOpenGalleryClick} actionItem={openGallery} />
					{imageEdit &&
						imageEditRoute &&
						(imageEdit.disabledHint ? (
							<OpenGalleryLink
								icon="edit"
								actionItem={{
									disabledHint: imageEdit.disabledHint,
									links: [],
								}}
							/>
						) : (
							<PTag icon="edit" variant="primary">
								<Link to={imageEditRoute}>{imageEdit.label}</Link>
							</PTag>
						))}
				</MediaLinksContainer>
			</PrimaryPicture>
			<PictureStatic picture={images[1]} size={320} />
			<PictureStatic picture={images[2]} size={320} />
			<PictureStatic picture={images[3]} size={320} />
		</Wrapper>
	)
}

const Wrapper = styled.div`
	display: grid;
	gap: ${spacingStaticXs};
	grid-template-columns: repeat(3, minmax(0, 1fr));
`

const PrimaryPicture = styled.div`
	position: relative;
	grid-column: 1 / -1;
	aspect-ratio: 4 / 3;
`

const AbsoluteOverlay = styled.div`
	position: absolute;
	top: ${spacingStaticSm};
`

const MediaLinksContainer = styled(AbsoluteOverlay)`
	display: flex;
	align-items: start;
	gap: ${spacingStaticSm};
	inset-inline-end: ${spacingStaticSm};
`

const TotalImageCount = styled(AbsoluteOverlay)`
	inset-inline-start: ${spacingStaticSm};
	opacity: 0.8;
`
const TotalImageCountTag = TotalImageCount.withComponent(PTag)
